import React, { useState, useEffect, useContext, useMemo, useCallback, useDeferredValue } from 'react'
import { Link, useLocation } from 'react-router-dom' // 🚀 useLocation 
import { ChevronLeft, ChevronRight, ChevronFirst, ChevronLast, Search, Grid, List } from 'lucide-react'
import { IoIosArrowDown } from 'react-icons/io'
import { DataContext } from '../Context/DataContext'
import { useSEO } from './Seo'
import AOS from 'aos'
import { Helmet } from 'react-helmet-async'

const STORAGE_KEY = "gameListPage"

const createSlug = (text) => {
  return text
    ?.toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const GameCardItem = React.memo(({ element, index, viewMode, currentPage }) => {
  const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  return (
    <li className="list-none">
      <Link
        to={`/play/${createSlug(element.name)}`} state={{ game: element }}
        data-aos="fade-up"
        data-aos-delay={index * 50}
        data-aos-duration="500"
        onClick={(e) => {
          if (!element.mobile && isMobileDevice) {
            e.preventDefault();
            alert("⚠️ This game is PC Only. It cannot run on mobile.");
            return;
          }
          try {
            localStorage.setItem(STORAGE_KEY, String(currentPage));
          } catch (e) { }
        }}
        className={`group transform hover:scale-105 transition-all duration-300 block ${viewMode === "list" ? "flex items-center gap-4 bg-white/5 rounded-xl p-4" : ""}`}
      >
        <div
          className={`relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 ${viewMode === "list" ? "xs:w-[150px] w-[100px] h-[100px] flex-shrink-0" : ""}`}
        >
          {!element.mobile && (
            <span className="absolute top-2 left-2 font-[700] bg-red-600 text-white text-xs px-2 py-[3px] rounded-lg z-20">
              PC Only
            </span>
          )}

          <picture>
            <source srcSet={element?.imageWebp} type="image/webp" />
            <img
              src={element?.image}
              alt={`Play ${element?.name} - Free online browser game at Game Zone Hub`}
              loading="lazy"
              decoding="async"
              className={`w-full object-cover transition-all duration-500 group-hover:scale-110 ${viewMode === "list" ? "h-full" : "sx:h-48 h-32"}`}
            />
          </picture>

          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

          <div
            className={`absolute ${viewMode === "list" ? "inset-0 flex items-center justify-center" : "bottom-3 left-3 right-3"}`}
          >
            <h3
              className={`font-bold text-white transition-all duration-300 ${viewMode === "list" ? "text-sm opacity-0 group-hover:opacity-100 hidden" : "sm:text-base text-sm opacity-0 group-hover:opacity-100 text-center"}`}
            >
              {element?.name}
            </h3>
          </div>
        </div>

        {viewMode === "list" && (
          <div className="flex-1">
            <h3 className="text-white font-semibold sm:text-lg text-[16px] group-hover:text-blue-400 transition-colors duration-300">
              {element?.name}
            </h3>
            <p className="text-gray-400 text-sm mt-1">Click to play instantly</p>
          </div>
        )}
      </Link>
    </li>
  );
});


const GameList = () => {
  const AllGame = useContext(DataContext) || []
  
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(STORAGE_KEY))
      return Number.isInteger(saved) && saved > 0 ? saved : 1
    } catch (e) {
      return 1
    }
  })

  const [gamesPerPage, setGamesPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState('grid')
  const [isLoading, setIsLoading] = useState(false)
  const deferredSearchTerm = useDeferredValue(searchTerm);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryQuery = searchParams.get("category");
    
    if (categoryQuery) {
      setSearchTerm(categoryQuery);
      setCurrentPage(1); 
    }
  }, [location.search]);


  useEffect(() => {
   import('aos').then((AOS) => {
    AOS.default.init({
      duration: 500,
      once: true,
    })
  })
}, [])


const filteredGames = useMemo(() => {
  if (!deferredSearchTerm.trim()) return AllGame;

  const term = deferredSearchTerm.toLowerCase();
  return AllGame.filter(game =>
    game.name?.toLowerCase().includes(term) || 
    game.category?.toLowerCase().includes(term) 
  );
}, [AllGame, deferredSearchTerm]);


const indexOfLastGame = useMemo(
  () => currentPage * gamesPerPage,
  [currentPage, gamesPerPage]
);

const indexOfFirstGame = useMemo(
  () => indexOfLastGame - gamesPerPage,
  [indexOfLastGame, gamesPerPage]
);

const currentGames = useMemo(
  () => filteredGames.slice(indexOfFirstGame, indexOfLastGame),
  [filteredGames, indexOfFirstGame, indexOfLastGame]
);

const totalPages = useMemo(
  () => Math.ceil((filteredGames.length || 0) / gamesPerPage),
  [filteredGames.length, gamesPerPage]
);


useEffect(() => {
    try {
     localStorage.setItem(STORAGE_KEY, String(currentPage));
    } catch {}
}, [currentPage]);

useEffect(() => {
  if (totalPages === 0) {
    currentPage !== 1 && setCurrentPage(1);
    return;
  }

  if (currentPage > totalPages) {
    setCurrentPage(totalPages);
  }
}, [totalPages, currentPage]);


  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  const handlePageChange = useCallback(
  (pageNumber) => {
    if (pageNumber < 1 || (totalPages && pageNumber > totalPages)) return;

    setIsLoading(true);

    try {
      localStorage.setItem(STORAGE_KEY, String(pageNumber));
    } catch {}

    setTimeout(() => {
      setCurrentPage(pageNumber);
      setIsLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      AOS.refresh();
    }, 200);
  },
  [totalPages]
);

 const handlePerPageChange = useCallback((value) => {
   setGamesPerPage(parseInt(value) || 10);
   setCurrentPage(1); 
 }, []);

 const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
 }, []);

  useEffect(() => {
   setIsLoading(true);

  const t = setTimeout(() => {
    setIsLoading(false);
    AOS.refresh();
  }, 250);

  return () => clearTimeout(t);
}, [viewMode]);

useSEO({
  title: deferredSearchTerm ? `Search Results for "${deferredSearchTerm}" | Game Zone Hub` : `Play Free Online Games – Page ${currentPage} | Game Zone Hub`,
  description: deferredSearchTerm 
    ? `Find and play the best ${deferredSearchTerm} games online for free. No downloads required.`
    : `Browse page ${currentPage} of our massive collection to play free online HTML5 games. Enjoy the best action, puzzle, and racing browser games instantly at Game Zone Hub.`,
  keywords: "play free online games, online games, play free online, free browser games, action games, puzzle games, racing games, HTML5 games, Game Zone Hub",
  canonical: `https://www.gamezonehub.in/games?page=${currentPage}`, 
});

const schemaData = useMemo(() => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": deferredSearchTerm ? `Search Results for ${deferredSearchTerm}` : `Play Free Online Games - Page ${currentPage}`,
  "description": "Discover and play the best free online browser games instantly. No downloads required.",
  "url": `https://www.gamezonehub.in/games?page=${currentPage}`,
  "mainEntity": {
    "@type": "ItemList",
    "itemListElement": currentGames?.map((game, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": game.name,
      "url": `https://www.gamezonehub.in/play/${createSlug(game.name)}`
    }))
  }
}), [deferredSearchTerm, currentPage, currentGames]);

  const renderPagination = () => {
    const pages = []
    const delta = 2
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (currentPage > 4) pages.push('...')
      const start = Math.max(2, currentPage - delta)
      const end = Math.min(totalPages - 1, currentPage + delta)
      for (let i = start; i <= end; i++) pages.push(i)
      if (currentPage < totalPages - 3) pages.push('...')
      if (totalPages > 1) pages.push(totalPages)
    }

    return pages.map((page, index) => {
      if (page === '...') {
        return (
          <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-400 select-none">
            ···
          </span>
        )
      }
      return (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          data-aos="zoom-in"
          data-aos-delay={index * 50}
          className={`relative px-4 py-2 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
            currentPage === page
              ? "bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-lg shadow-blue-500/40 scale-105"
              : "bg-white/10 backdrop-blur-sm text-gray-300 hover:bg-white/20 hover:text-white border border-white/20"
          }`}
        >
          {page}
        </button>
      )
    })
  }

  const renderQuickJump = () => {
    const quickPages = []
    const step = Math.ceil(totalPages / 5) || 1
    for (let i = step; i < totalPages; i += step) quickPages.push(i)
    return quickPages.map((page, i) => (
      <button
        key={`quick-${page}`}
        onClick={() => handlePageChange(page)}
        data-aos="fade-up"
        data-aos-delay={i * 80}
        className="px-2 py-1 text-xs rounded-md bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-all duration-200"
      >
        {page}
      </button>
    ))
  }

  return (
   <div className='bg-gradient-to-br lg:px-8 from-gray-900 via-black to-gray-800 min-h-screen sm:pt-[120px] pt-[100px] '>

      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
    <div className="container mx-auto px-4">
      
      <div className="text-center mb-8 sm:mt-0 mt-3" >
        <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300 sm:mb-4 mb-2">
           {deferredSearchTerm ? `Search: ${deferredSearchTerm}` : 'Play Free Online Games'}
        </h1>
          <p className="text-gray-400 sm:text-lg text-[16px] max-w-3xl mx-auto leading-relaxed">
            Welcome to the ultimate collection to <strong>play free online games</strong>. 
            Browse thousands of top-rated HTML5 titles across all genres. Whether you love high-speed <strong>racing games</strong>, 
            brain-teasing <strong>puzzles</strong>, or thrilling <strong>action adventures</strong>, 
            Game Zone Hub has it all. Just click and <strong>play free online</strong> instantly on any device with absolutely no downloads required.
          </p>
      </div>

      <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-white/10" data-aos="zoom-in">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative lg:w-auto md:w-3/4 sm:w-3/4 w-full flex-1 max-w-md" data-aos="fade-right">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange} 
              aria-label="Search games"
              className="w-full pl-10 pr-4 py-3 bg-black/30 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
              placeholder="Search by name or category (e.g. girls, car)..."
            />
          </div>

          <div className="flex items-center gap-4" data-aos="fade-left">
            <div className="flex items-center gap-2">
              <span className="text-gray-300 text-sm">Show:</span>
              <div className='relative'>
                <select
                  value={gamesPerPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                  className="bg-black/30 border border-white/20 rounded-lg ps-3 pe-7 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                  aria-label="Games per page"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <IoIosArrowDown className='text-white absolute sm:text-[16px] text-[14px] right-2 top-1/2 -translate-y-1/2 pointer-events-none' />
              </div>
            </div>

            <div className="flex bg-black/30 rounded-lg border border-white/20 p-1">
              <button
                onClick={() => { if (viewMode !== 'grid') { setViewMode('grid') } }}
                className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
                aria-label="Grid View"
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => { if (viewMode !== 'list') { setViewMode('list') } }}
                className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
                aria-label="List View"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-12" data-aos="zoom-in">
          <div className="animate-spin rounded-full h-12 w-12 border-b-[4px] border-blue-500"></div>
        </div>
      )}

      {!isLoading && (
        <ul
          className={`${viewMode === 'grid'
            ? 'grid grid-cols-2 sx:grid-cols-2 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6'
            : 'flex flex-col gap-4'} sm:pb-8 pb-10`}
        >
         {currentGames.length > 0 ? (
           currentGames.map((element, index) => (
             <GameCardItem 
               key={`${element.name}-${index}`} 
               element={element} 
               index={index} 
               viewMode={viewMode} 
               currentPage={currentPage} 
             />
           ))
          ) : (
              <div className="col-span-full text-center py-12" data-aos="fade-up">
                <div className="text-6xl mb-4">🎮</div>
                <h3 className="text-2xl font-bold text-white mb-2">No games found</h3>
                <p className="text-gray-400">Try adjusting your search terms</p>
              </div>
          )}
            
         </ul>
       )}

      {!isLoading && totalPages > 1 && (
        <div data-aos="fade-up">
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 ">
            <div className="flex flex-wrap justify-center items-center gap-2 mb-4">
              <button disabled={currentPage === 1} onClick={() => handlePageChange(1)} className="p-2 rounded-xl bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white disabled:opacity-30 transition-all duration-300 hover:scale-105">
                <ChevronFirst size={18} />
              </button>
              <button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white disabled:opacity-30 transition-all duration-300 hover:scale-105">
                <ChevronLeft size={18} /> Prev
              </button>
              <div className="flex items-center gap-1 flex-wrap">{renderPagination()}</div>
              <button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white disabled:opacity-30 transition-all duration-300 hover:scale-105">
                Next <ChevronRight size={18} />
              </button>
              <button disabled={currentPage === totalPages} onClick={() => handlePageChange(totalPages)} className="p-2 rounded-xl bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white disabled:opacity-30 transition-all duration-300 hover:scale-105">
                <ChevronLast size={18} />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
              {totalPages > 10 && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Quick jump:</span>
                  <div className="flex gap-1">{renderQuickJump()}</div>
                </div>
              )}
              <div className="flex items-center gap-4 text-gray-400">
                <span>Page {currentPage} of {totalPages}</span>
                <div className="h-4 w-px bg-gray-600"></div>
                <span>{filteredGames.length} total games</span>
              </div>
            </div>

            <div className="mt-4">
              <div className="w-full bg-gray-700 rounded-full h-1">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-cyan-400 h-1 rounded-full transition-all duration-500"
                  style={{ width: `${(currentPage / (totalPages || 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
          <div className='pb-11'></div>
        </div>
      )}

      <div className="bg-[#111827] rounded-xl  border-t border-white/10 mt-12 pt-16 pb-16 px-4" data-aos="fade-up">
        <div className="max-w-6xl mx-auto space-y-12">
          
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">Your Ultimate Hub to Play Free Online Games</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            <div className="space-y-6 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>
                Welcome to <strong>Game Zone Hub</strong>, the Internet's premier destination for unlimited gaming entertainment. Whether you are searching to <strong>play free online games</strong> during your break, or you want to dive deep into a complex logic puzzle, we have carefully curated the best titles just for you. Our HTML5 platform requires absolutely no downloads, no installations, and no complicated setups.
              </p>
              <p>
                Our massive library is updated regularly with new, exciting browser games. From high-speed 3D racing adventures to relaxing brain-teasers, our games are designed to run flawlessly across all your devices, including desktop PCs, laptops, iPads, tablets, and smartphones.
              </p>
              <div className="bg-white/5 p-6 rounded-2xl border border-white/10 border-l-4 border-l-blue-500">
                <h3 className="text-xl font-bold text-white mb-2">Why Choose Us?</h3>
                <ul className="list-none space-y-2">
                  <li className="flex flex-wrap items-center gap-2">✅ <strong>100% Free:</strong> No paywalls or premium subscriptions.</li>
                  <li className="flex flex-wrap items-center gap-2">✅ <strong>No Downloads:</strong> Save your device's storage space.</li>
                  <li className="flex flex-wrap items-center gap-2">✅ <strong>Cross-Platform:</strong> Play on any modern web browser.</li>
                  <li className="flex flex-wrap items-center gap-2">✅ <strong>Safe & Secure:</strong> All games are rigorously tested for malware.</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6 text-gray-300 leading-relaxed text-sm sm:text-base">
              <h3 className="text-2xl font-bold text-white mb-4">Discover Top Game Categories</h3>
              <p>
                Navigating our collection is incredibly easy. We host an extensive variety of categories to ensure every type of gamer finds their perfect match:
              </p>
              <ul className="space-y-4">
                <li className="flex gap-4 p-4 bg-black/30 rounded-xl hover:bg-black/50 transition border border-white/5">
                  <div className="bg-blue-500/20 text-blue-400 p-2 rounded-lg h-fit">🎮</div>
                  <div>
                    <strong className="text-white block mb-1">Action & Arcade</strong>
                    Get your adrenaline pumping with fast-paced combat, classic arcade remakes, and intense multiplayer survival challenges. 
                  </div>
                </li>
                <li className="flex gap-4 p-4 bg-black/30 rounded-xl hover:bg-black/50 transition border border-white/5">
                  <div className="bg-purple-500/20 text-purple-400 p-2 rounded-lg h-fit">🧩</div>
                  <div>
                    <strong className="text-white block mb-1">Puzzle & Logic</strong>
                    Test your brain power with intricate puzzles, match-3 mechanics, physics-based challenges, and strategic board games.
                  </div>
                </li>
                <li className="flex gap-4 p-4 bg-black/30 rounded-xl hover:bg-black/50 transition border border-white/5">
                  <div className="bg-cyan-500/20 text-cyan-400 p-2 rounded-lg h-fit">🏎️</div>
                  <div>
                    <strong className="text-white block mb-1">Racing & Driving</strong>
                    Feel the speed in breathtaking 3D driving simulators, off-road dirt track challenges, and competitive asphalt races.
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12 bg-gradient-to-r from-blue-900/30 to-purple-900/30 sm:p-8 p-6 rounded-3xl border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-4">Ready to start your adventure?</h3>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              Simply scroll up, find an HTML5 browser game that catches your eye, and click to play instantly. Remember, everything on Game Zone Hub is completely free forever.
            </p>
          </div>

        </div>
      </div>
    </div>
   </div>
  )
}

export default GameList